import { formatDate, formatPhoneNumber, USER_TYPE_MAPPING } from "../../../constants";
import { buildExportFilename, fetchAllPaginated } from "../../../utils/paginatedExport";

// The two sets of users the Users page can show / export.
export const USER_EXPORT_TABS = [
  { key: "active", label: "Active Users", icon: "fa-user-check" },
  { key: "inactive", label: "Inactive Users", icon: "fa-user-slash" },
];

/**
 * Builds the list request for a user tab. Shared by the table and the export
 * so the two can never diverge on endpoint or params.
 */
export const buildUserListRequest = ({
  tab,
  page,
  limit,
  searchTerm = "",
  userType = "ALL",
  company = "ALL",
}) => {
  const url = `${process.env.REACT_APP_base_url}api/masterAdmin/users`;

  const requestData = {
    auth_code: process.env.REACT_APP_AUTH_CODE,
    status: tab === "active" ? "1" : "0",
    page: page.toString(),
    limit: limit.toString(),
  };

  if (searchTerm.trim()) {
    requestData.search = searchTerm.trim();
  }

  if (userType !== "ALL") {
    requestData.userType = userType;
  }

  if (company !== "ALL") {
    requestData.company = company;
  }

  return { url, requestData };
};

/** Fetches users for a tab by looping through the paginated endpoint. */
export const fetchAllUsers = ({
  tab,
  searchTerm = "",
  userType = "ALL",
  company = "ALL",
  fromPage,
  toPage,
  pageSize,
  onProgress,
  isCancelled,
}) =>
  fetchAllPaginated({
    buildRequest: (page, limit) =>
      buildUserListRequest({ tab, page, limit, searchTerm, userType, company }),
    fromPage,
    toPage,
    pageSize,
    onProgress,
    isCancelled,
  });

/**
 * Describes the filters the export will apply, for the modal's summary.
 * All three filters apply to both tabs, so there are no per-tab exclusions.
 */
export const buildUserFilterSummary = ({ searchTerm = "", userType = "ALL", companyName = "" }) => {
  const items = [];

  if (searchTerm.trim()) {
    items.push({ label: "Search", value: `"${searchTerm.trim()}"` });
  }

  if (userType !== "ALL") {
    items.push({ label: "User Type", value: USER_TYPE_MAPPING[userType] || userType });
  }

  if (companyName) {
    items.push({ label: "Company", value: companyName });
  }

  return { items, notes: [] };
};

export const buildUserExportFilename = ({
  tab,
  searchTerm = "",
  userType = "ALL",
  companyName = "",
  scopeParts = [],
}) => {
  const parts = [tab];

  if (searchTerm.trim()) {
    parts.push("search", searchTerm);
  }

  if (userType !== "ALL") {
    parts.push(USER_TYPE_MAPPING[userType] || userType);
  }

  if (companyName) {
    parts.push(companyName);
  }

  return buildExportFilename("users", [...parts, ...scopeParts]);
};

/**
 * CSV columns, mirroring the Users table. Every displayed column maps 1:1 to a
 * field (the backend already flattens `company` to a name string); the Action
 * column is omitted.
 */
export const USER_EXPORT_COLUMNS = [
  { header: "Sr. No.", value: (row, index) => index + 1 },
  { header: "Username", value: (row) => row.username },
  { header: "Email", value: (row) => row.email },
  { header: "Phone No", value: (row) => formatPhoneNumber(row?.countryCode, row?.phoneNo) },
  { header: "Company", value: (row) => row.company || "-" },
  { header: "User Type", value: (row) => USER_TYPE_MAPPING[row.type] || "Unknown" },
  { header: "Status", value: (row) => (row.status === 1 ? "Active" : "Inactive") },
  { header: "Created At", value: (row) => formatDate(row?.createdAt) },
  { header: "Deactivated At", value: (row) => formatDate(row?.deactivatedAt) },
  { header: "Reactivated At", value: (row) => formatDate(row.reactivatedAt) },
];
